/** @module Correct one result without leaving the camera: card, variant, language, book or drop. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, ExternalLink, Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { CardPicker, startingQuery } from "@/components/card-picker";
import { type Choice, defaultVariant, ResolveForm } from "@/components/resolve-form";
import { ScanStatus } from "@/components/ui/controls";
import { Sheet } from "@/components/ui/overlays";
import type { ScanEntry } from "@/features/scan/use-scan-session";
import { printedNumber, readNumber, useCard } from "@/lib/cards";

interface Candidate {
  print_id: string;
  score: number;
  from_ocr?: boolean;
}

/** The status a strip item shows: ✓ booked, ? to check, ✕ no match, ⏳ still working. */
export function entryStatus(entry: ScanEntry): "auto" | "review" | "none" | "busy" {
  if (entry.booked) return "auto";
  if (entry.state === "failed") return "none";
  if (entry.state !== "done") return "busy";
  if (entry.decision === "auto") return "auto";
  if (entry.decision === "no_match") return "none";
  return "review";
}

async function fetchScan(scanId: string) {
  const { data, response } = await api.GET("/api/v1/scans/{scan_id}", {
    params: { path: { scan_id: scanId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

function Body({
  entry,
  onDismiss,
  onBooked,
  onReopened,
}: {
  entry: ScanEntry;
  onDismiss: () => void;
  onBooked: () => void;
  /** A booking was taken back: the photo is an open case again. */
  onReopened: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const scan = useQuery({
    queryKey: ["scan", entry.scanId, entry.state],
    queryFn: () => fetchScan(entry.scanId ?? ""),
    enabled: Boolean(entry.scanId) && entry.state === "done",
  });
  const result = scan.data?.result;
  const candidates = (result?.candidates ?? []) as unknown as Candidate[];
  const [picked, setPicked] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const chosen = picked ?? result?.chosen_print_id ?? candidates[0]?.print_id ?? null;
  const card = useCard(chosen);
  const [choice, setChoice] = useState<Choice>({
    variantId: null,
    // Empty until chosen: the scan's own suggestion arrives only after this first render.
    language: "",
    condition: "NM",
  });
  const variants = card.data?.variants ?? [];
  const suggested = chosen === result?.chosen_print_id ? (result.chosen_variant_id ?? null) : null;
  const variantId = variants.some((variant) => variant.id === choice.variantId)
    ? choice.variantId
    : (defaultVariant(variants, suggested)?.id ?? null);
  // An empty choice means "not picked yet"; the suggestion, then the app language, stand in.
  const language = choice.language || (result?.language_suggestion ?? locale);
  const top = candidates.find((candidate) => candidate.print_id === chosen);
  const status = entryStatus(entry);

  const book = useMutation({
    mutationFn: async () => {
      if (!entry.scanId || !chosen || !variantId) throw new Error("nothing chosen");
      const { data, response } = await api.POST("/api/v1/scans/{scan_id}/resolve", {
        params: { path: { scan_id: entry.scanId } },
        body: { print_id: chosen, variant_id: variantId, language, condition: choice.condition },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: onBooked,
    onError: (cause) => {
      // Settled in the review meanwhile: as good as booked from here.
      if (cause instanceof ApiError && cause.code === "scan_already_resolved") onBooked();
    },
  });

  // Taking back a booking: the worker's own ones always, a person's right after it.
  const reopen = useMutation({
    mutationFn: async () => {
      const { response } = await api.POST("/api/v1/scans/{scan_id}/reopen", {
        params: { path: { scan_id: entry.scanId ?? "" } },
      });
      if (!response.ok) throw problemFrom(response);
    },
    onSuccess: onReopened,
  });
  // Deleting needs a finished scan: the worker must not lose the file mid-recognition.
  const removable = Boolean(entry.scanId) && (entry.state === "done" || entry.state === "failed");

  const read = readNumber(result?.ocr);
  const statusText = {
    auto: t("scan.correct.booked"),
    review: t("scan.correct.review"),
    none: entry.state === "failed" ? t("scan.result.failed") : t("scan.correct.noMatch"),
    busy: t("scan.correct.working"),
  }[status];

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="row items-start gap-3">
        <span className="card-shape block w-[84px] shrink-0">
          {chosen ? (
            <CardFace printId={chosen} name={card.data?.name} />
          ) : (
            <img src={entry.thumbnail} alt="" className="card-shape w-full object-cover" />
          )}
        </span>
        <div className="flex min-w-0 grow flex-col gap-1">
          <span className="row">
            <ScanStatus state={status} />
            <span className="t-sm">{statusText}</span>
          </span>
          {card.data ? (
            <>
              <b className="truncate text-[17px]">{card.data.name}</b>
              <span className="t-sm t-muted truncate">
                {card.data.set_name ? `${card.data.set_name} · ` : ""}
                <span className="num">{printedNumber(card.data)}</span>
              </span>
            </>
          ) : null}
          {top ? (
            <span className="t-xs t-muted">
              {result?.ocr_confirms && top.print_id === result.chosen_print_id
                ? t("scan.correct.ocrConfirms")
                : read
                  ? t("scan.correct.ocrRead", { number: read })
                  : t("scan.correct.ocrNone")}
              {" · "}
              {t("review.score")}{" "}
              <span className="num">
                {top.score.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      {status !== "busy" && status !== "auto" ? (
        <>
          {candidates.length > 1 ? (
            <div className="flex gap-[6px] overflow-x-auto pb-1">
              {candidates.slice(0, 5).map((candidate) => (
                <button
                  key={candidate.print_id}
                  type="button"
                  className="cand-card w-16 shrink-0 gap-1 p-1"
                  aria-pressed={candidate.print_id === chosen}
                  title={candidate.print_id}
                  onClick={() => {
                    setPicked(candidate.print_id);
                  }}
                >
                  <span className="card-shape block">
                    <CardFace printId={candidate.print_id} lazy />
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {searching ? (
            <CardPicker
              initialQuery={startingQuery(result?.ocr)}
              onPick={(printId) => {
                setPicked(printId);
                setSearching(false);
              }}
            />
          ) : null}
          <ResolveForm
            variants={variants}
            choice={{ ...choice, variantId, language }}
            locale={locale}
            suggestedLanguage={result?.language_suggestion}
            onChange={setChoice}
          />
        </>
      ) : null}

      {book.isError &&
      !(book.error instanceof ApiError && book.error.code === "scan_already_resolved") ? (
        <p role="alert" className="t-sm loss">
          {t("review.error.generic")}
        </p>
      ) : null}

      {status === "auto" && entry.scanId ? (
        <div className="row gap-2">
          <button
            type="button"
            className="btn btn-secondary grow"
            disabled={reopen.isPending}
            onClick={() => {
              reopen.mutate();
            }}
          >
            <Search />
            {t("scan.correct.reopen")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={reopen.isPending}
            onClick={onDismiss}
          >
            <X />
            {t("scan.correct.remove")}
          </button>
        </div>
      ) : null}
      {reopen.isError ? (
        <p role="alert" className="t-sm loss">
          {reopen.error instanceof ApiError && reopen.error.code === "scan_undo_expired"
            ? t("scan.correct.tooLate")
            : t("review.error.generic")}
        </p>
      ) : null}

      {status === "auto" ? null : (
        <div className="row gap-2">
          {status !== "busy" ? (
            <button
              type="button"
              className="btn btn-secondary grow"
              aria-pressed={searching}
              onClick={() => {
                setSearching((current) => !current);
              }}
            >
              <Search />
              {t("scan.correct.otherCard")}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-danger"
            disabled={!removable || book.isPending}
            onClick={onDismiss}
          >
            <X />
            {t("scan.correct.remove")}
          </button>
        </div>
      )}
      <p className="t-xs t-muted">{t("scan.correct.removeHint")}</p>
      {(status === "review" || status === "none") && entry.scanId ? (
        // The full review: large comparison, zoom, corners, other prints.
        <Link
          to="/review"
          search={{ scan: entry.scanId }}
          className="btn btn-ghost btn-sm self-start"
        >
          <ExternalLink />
          {t("scan.correct.openReview")}
        </Link>
      ) : null}
      {status === "review" || status === "none" ? (
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={!chosen || !variantId || book.isPending || entry.state !== "done"}
          onClick={() => {
            book.mutate();
          }}
        >
          <Check />
          {t("scan.correct.confirm")}
        </button>
      ) : null}
    </div>
  );
}

export function CorrectionSheet({
  entry,
  onClose,
  onDismiss,
  onBooked,
  onReopened,
}: {
  entry: ScanEntry | null;
  onClose: () => void;
  onDismiss: (entry: ScanEntry) => void;
  onBooked: (entry: ScanEntry) => void;
  onReopened: (entry: ScanEntry) => void;
}) {
  const { t } = useTranslation();
  return (
    <Sheet open={entry !== null} onClose={onClose} label={t("scan.correct.title")}>
      {entry ? (
        <div className="flex flex-col gap-[14px]">
          <Body
            key={entry.key}
            entry={entry}
            onDismiss={() => {
              onDismiss(entry);
              onClose();
            }}
            onBooked={() => {
              onBooked(entry);
              onClose();
            }}
            onReopened={() => {
              onReopened(entry);
            }}
          />
          <button type="button" className="btn btn-ghost btn-block" onClick={onClose}>
            {t("scan.correct.done")}
          </button>
        </div>
      ) : null}
    </Sheet>
  );
}
